import { useEffect, useState } from 'react'
import { safeGetItem, safeRemoveItem, safeSetItem } from './safeStorage'

// Persisted create-flow settings are namespaced under `create_` so they never
// collide with ChatCreateContext, which stores its own `model`/`charImage`/etc.
// on the same origin.
const persistKey = (name) => `create_${name}`

// Decoders/encoders for usePersistedState (localStorage only stores strings).
export const asNumber = { decode: Number }
export const asBool = { decode: (raw) => raw === 'true' }
// Only hosted URLs are worth persisting for the character image — base64 data
// URLs are transient and would blow the localStorage quota, so skip them.
export const asHttpUrl = {
  decode: (raw) => (raw.startsWith('http') ? raw : null),
  encode: (value) => (value && value.startsWith('http') ? value : null),
}

// A useState whose value is mirrored to localStorage: reads the stored value
// (decoded) on mount, falling back to `fallback` when absent, and writes every
// change back (encoded). `fallback` may be a function for lazy defaults (e.g. a
// random seed); an encoder returning null/undefined removes the key. SSR-safe.
//
// `resetOnce` names a marker key: the FIRST time it's absent the stored value is
// discarded in favour of `fallback` and the marker is written. That's how a new
// default reaches users who already have the old one persisted — without it their
// stored choice would pin them to the previous default forever. Every choice they
// make after the migration persists normally.
export function usePersistedState(
  name,
  fallback,
  { decode, encode, resetOnce } = {},
) {
  const key = persistKey(name)
  const [value, setValue] = useState(() => {
    const resolve = () =>
      typeof fallback === 'function' ? fallback() : fallback
    if (typeof window === 'undefined') return resolve()
    if (resetOnce) {
      const marker = persistKey(resetOnce)
      if (safeGetItem(marker) === null) {
        safeSetItem(marker, '1')
        return resolve()
      }
    }
    const raw = safeGetItem(key)
    if (raw === null) return resolve()
    return decode ? decode(raw) : raw
  })
  useEffect(() => {
    if (typeof window === 'undefined') return
    const raw = encode ? encode(value) : value
    if (raw === null || raw === undefined) safeRemoveItem(key)
    else safeSetItem(key, String(raw))
  }, [key, value])
  return [value, setValue]
}
